import type { RequestOpts } from '@oazapfts/runtime';
import { Err, Ok } from 'ts-results-es';

import { appConfig } from '@/config';
import type { Auth } from '@/entities/auth';
import type { Me } from '@/entities/me';
import type { IDocumentRepository } from '@/modules/documents/domain/repositories/document.repository';
import type { RequestOptions } from '@/modules/documents/domain/repositories/utils/request-options';

import { FeApi, handle } from './fetch-client';
import { DocumentRepositoryMapper } from './mappers/document.repository.mapper';

export const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
  'image/jpeg',
  'image/png',
  'image/gif',
  'text/plain',
];
export const MAX_FILE_SIZE_MB = 100;
export const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;

export class FeApiDocumentRepository implements IDocumentRepository {
  constructor(
    private me: Me,
    private auth: Auth,
  ) {}

  private async getOpts(): Promise<RequestOpts> {
    const accessToken = await this.auth.getAccessToken();

    return {
      baseUrl: `${appConfig.bffUrl}/plex`,
      headers: {
        authorization: `Bearer ${accessToken}`,
        'x-user-email': this.me.email,
        'x-user-name': this.me.displayName,
      },
    };
  }

  async create(
    input: IDocumentRepository.CreateInput,
    requestOptions?: RequestOptions,
  ): Promise<IDocumentRepository.CreateOutput> {
    const { file } = input.document;

    if (file.size > MAX_FILE_SIZE) {
      return Err({
        type: 'FILE_TOO_BIG',
        filename: file.name,
        limit: MAX_FILE_SIZE,
      });
    }
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      return Err({
        type: 'FILE_INVALID_TYPE',
        given: file.type,
      });
    }

    const opts = await this.getOpts();
    const body = DocumentRepositoryMapper.create.bodyFromInput(input);
    const response = await FeApi.recordsV1DocumentsUploadCreate(body, {
      ...opts,
      signal: requestOptions?.signal,
    });

    return handle(response).to<IDocumentRepository.CreateOutput>({
      201() {
        return Ok(undefined);
      },
      400() {
        return Err({ type: 'EXTERNAL_ERROR', description: undefined });
      },
      422({ error }) {
        return Err({ type: 'EXTERNAL_ERROR', description: error });
      },
    });
  }

  async delete(
    input: IDocumentRepository.DeleteInput,
    requestOptions?: RequestOptions,
  ): Promise<IDocumentRepository.DeleteOutput> {
    const opts = await this.getOpts();
    const body = DocumentRepositoryMapper.deleteById.bodyFromInput(input);
    const response = await FeApi.recordsV1DocumentsDeleteDestroy(body, {
      ...opts,
      signal: requestOptions?.signal,
    });

    return handle(response).to<IDocumentRepository.DeleteOutput>({
      204() {
        return Ok(undefined);
      },
      404() {
        return Err({ type: 'NOT_FOUND' });
      },
    });
  }

  async getAll(
    input: IDocumentRepository.GetAllInput,
    requestOptions?: RequestOptions,
  ) {
    const opts = await this.getOpts();
    const body = DocumentRepositoryMapper.getAll.bodyFromInput(input);
    const response = await FeApi.recordsV1DocumentsListRetrieve(body, {
      ...opts,
      signal: requestOptions?.signal,
    });

    return handle(response).to<IDocumentRepository.GetAllOutput>({
      async 200(data) {
        const documents =
          await DocumentRepositoryMapper.getAll.outputFromData(data);

        return documents;
      },
    });
  }

  async download(
    input: IDocumentRepository.DownloadInput,
    requestOptions?: RequestOptions | undefined,
  ): Promise<IDocumentRepository.DownloadOutput> {
    const opts = await this.getOpts();
    const body = DocumentRepositoryMapper.download.bodyFromInput(input);
    const response = await FeApi.recordsV1DocumentsDownloadRetrieve(body, {
      ...opts,
      signal: requestOptions?.signal,
    });

    return handle(response).to<IDocumentRepository.DownloadOutput>({
      200(data) {
        const file = DocumentRepositoryMapper.download.outputFromData(
          data,
          input.document,
        );

        return Ok({ file });
      },
      404() {
        return Err({ type: 'NOT_FOUND' });
      },
    });
  }

  async update(
    input: IDocumentRepository.UpdateInput,
    requestOptions?: RequestOptions,
  ): Promise<IDocumentRepository.UpdateOutput> {
    const opts = await this.getOpts();
    const body = DocumentRepositoryMapper.update.bodyFromInput(input);
    const response = await FeApi.recordsV1DocumentsUpdateUpdate(body, {
      ...opts,
      signal: requestOptions?.signal,
    });

    return handle(response).to<IDocumentRepository.UpdateOutput>({
      204() {
        return Ok(undefined);
      },
      404() {
        return Err({ type: 'NOT_FOUND' });
      },
      400() {
        return Err({ type: 'EXTERNAL_ERROR', description: undefined });
      },
    });
  }
}
